import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import { ToastManager } from './ToastManager'
import { SystemIcon } from '@shared/designSystem/SystemIcon'
import { designSystemStyles } from '@shared/styles/designSystemStyles'
import styles from '@shared/styles/components/Toast.module.css'

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info'

interface ToastContextType {
    showToast: (message: string, severity?: ToastSeverity) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToast must be used within ToastProvider')
    }
    return context
}

interface ToastProviderProps {
    children: ReactNode
    autoHideDurationInSeconds?: number
}

const closeAnimationMs = 180

const severityIconType = (severity: ToastSeverity): 'info' | 'check' | 'warning-triangle' => {
    switch (severity) {
        case 'success':
            return 'check'
        case 'error':
        case 'warning':
            return 'warning-triangle'
        case 'info':
            return 'info'
    }
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
        children,
        autoHideDurationInSeconds,
    }) => {

    const defaultDurationMs = autoHideDurationInSeconds ? autoHideDurationInSeconds * 1000 : 6000

    const [open, setOpen] = useState(false)
    const [message, setMessage] = useState('')
    const [severity, setSeverity] = useState<ToastSeverity>('info')
    const [autoHideDuration, setAutoHideDuration] = useState(defaultDurationMs)
    const [toastId, setToastId] = useState(0)
    const [isVisible, setIsVisible] = useState(false)
    const [isClosing, setIsClosing] = useState(false)
    const [hasEntered, setHasEntered] = useState(false)

    const showToast = useCallback((msg: string, sev: ToastSeverity = 'info', autoHideInSeconds?: number) => {
        setMessage(msg)
        setSeverity(sev)
        setAutoHideDuration(autoHideInSeconds ? autoHideInSeconds * 1000 : defaultDurationMs)
        setToastId((id) => id + 1)
        setOpen(true)
    }, [defaultDurationMs])

    const handleClose = useCallback(() => {
        setOpen(false)
    }, [])

    useEffect(() => {
        if (open) {
            setIsVisible(true)
            setIsClosing(false)
        } else if (isVisible) {
            setIsClosing(true)
            const timeout = window.setTimeout(() => {
                setIsClosing(false)
                setIsVisible(false)
            }, closeAnimationMs)
            return () => window.clearTimeout(timeout)
        }
    }, [open, isVisible])

    useEffect(() => {
        if (!isVisible) {
            setHasEntered(false)
            return
        }
        // requestAnimationFrame does not fire in hidden tabs; the timeout guarantees the toast still appears there
        const frame = window.requestAnimationFrame(() => setHasEntered(true))
        const fallback = window.setTimeout(() => setHasEntered(true), 100)
        return () => {
            window.cancelAnimationFrame(frame)
            window.clearTimeout(fallback)
        }
    }, [isVisible])

    useEffect(() => {
        if (!open) {
            return
        }
        const timeout = window.setTimeout(handleClose, autoHideDuration)
        return () => window.clearTimeout(timeout)
    }, [open, toastId, autoHideDuration, handleClose])

    useEffect(() => {
        const manager = ToastManager.shared()
        manager.setCallback((msg, sev, autoHideInSeconds) => {
            showToast(msg, (sev as ToastSeverity) ?? 'info', autoHideInSeconds)
        })

        return () => {
            manager.setCallback(undefined)
        }
    }, [showToast])

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {isVisible && (
                <div className={[styles['toast-viewport'], 'DSToastViewport'].join(' ')}>
                    <div
                        className={[
                            styles.toast,
                            styles[`toast-${severity}`],
                            isClosing ? styles['toast-closing'] : (hasEntered ? styles['toast-open'] : undefined),
                            'DSToast'
                        ].filter(Boolean).join(' ')}
                        role={severity === 'error' || severity === 'warning' ? 'alert' : 'status'}
                    >
                        <span className={styles['toast-icon']}>
                            <SystemIcon type={severityIconType(severity)} color={designSystemStyles.colorToastText} />
                        </span>
                        <span className={styles['toast-message']}>{message}</span>
                        <button
                            type="button"
                            className={styles['toast-close']}
                            onClick={handleClose}
                            aria-label={'Cerrar'.localized()}
                        >
                            <SystemIcon type="close" color={designSystemStyles.colorToastText} />
                        </button>
                    </div>
                </div>
            )}
        </ToastContext.Provider>
    )
}
